import { Controller } from "@hotwired/stimulus";

interface OverlayElement extends HTMLElement {
  open: boolean;
  /** The internal <dialog>, which wa-drawer and wa-dialog name differently. */
  drawer?: HTMLDialogElement;
  dialog?: HTMLDialogElement;
}

/**
 * Controls a Web Awesome overlay, like <wa-dialog> or <wa-drawer>, that
 * opens and closes via its `open` property.
 *
 * Open/close events may carry `detail: { id }` to address a single overlay;
 * events without an id apply to every overlay they reach.
 */
export class OverlayController extends Controller<OverlayElement> {
  static override values = {
    id: String,
    modal: { type: Boolean, default: true },
    linkable: { type: Boolean, default: false },
  };
  declare readonly idValue: string;
  declare readonly modalValue: boolean;
  declare readonly linkableValue: boolean;

  override connect(): void {
    if (this.linkableValue && window.location.hash === `#${this.idValue}`) {
      // The property only opens the overlay once the element is upgraded.
      void customElements
        .whenDefined(this.element.localName)
        .then(() => this.setOpen(undefined, true));
    }
  }

  open(event?: CustomEvent): void {
    this.setOpen(event, true);
  }

  close(event?: CustomEvent): void {
    this.setOpen(event, false);
  }

  /** Tell the content that the overlay closed, as `overlay:hidden`. */
  hidden(event: Event): void {
    // Popovers and tooltips in the overlay send the same event.
    if (event.target !== this.element) return;
    if (this.linkableValue && window.location.hash === `#${this.idValue}`) {
      const { pathname, search } = window.location;
      history.replaceState(history.state, "", pathname + search);
    }
    this.dispatch("hidden", { bubbles: false });
  }

  private setOpen(event: CustomEvent | undefined, open: boolean): void {
    // Ignore the event if it is intended for another overlay.
    if (event?.detail?.id && event.detail.id !== this.idValue) {
      return;
    }
    if (open && !this.modalValue) {
      this.makeDialogNonModal();
    }
    this.element.open = open;
    if (open && this.linkableValue) {
      history.replaceState(history.state, "", `#${this.idValue}`);
    }
  }

  /**
   * A modal dialog makes every other element inert, including the toast in
   * which a password manager offers to save a new credential. The component
   * calls showModal in more than one place, so the method is replaced
   * instead of the call.
   */
  private makeDialogNonModal(): void {
    const dialog = this.element.drawer ?? this.element.dialog;
    if (dialog && dialog.showModal !== dialog.show) {
      dialog.showModal = dialog.show.bind(dialog);
    }
  }
}

/**
 * Trigger that dispatches an `open` event addressed to the overlay with the same id value.
 *
 * Clicks on elements that match the `ignore` selector do not open the overlay.
 */
export class OverlayToggleController extends Controller {
  static override values = { id: String, ignore: String };
  declare readonly idValue: string;
  declare readonly ignoreValue: string;

  trigger(event: Event): void {
    if (
      this.ignoreValue &&
      event.target instanceof Element &&
      event.target.closest(this.ignoreValue)
    ) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.dispatch("open", { detail: { id: this.idValue } });
  }
}
